import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ChevronDown, HelpCircle, ArrowLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import useScrollToTop from '../hooks/useScrollToTop';

function FAQ() {
  useScrollToTop();
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqData = [
    {
      category: "General",
      questions: [
        {
          question: "What is ClaraAI?",
          answer: "ClaraAI is a revolutionary macOS productivity app featuring an AI-powered Vision Frame - a transparent, draggable window that provides contextual screen analysis and intelligent insights in real-time. It's designed to enhance your workflow with cutting-edge AI technology while maintaining privacy and security."
        },
        {
          question: "When will ClaraAI be available?",
          answer: "ClaraAI is currently in development and will launch on macOS first, with Windows support coming soon. Join our waitlist to be among the first to experience the future of productivity when we release early access."
        },
        {
          question: "What makes ClaraAI different from other productivity tools?",
          answer: "ClaraAI stands out with its unique Vision Frame technology, multi-AI integration (OpenAI, Gemini, Grok, and local Ollama), real-time audio transcription, and privacy-first approach. Unlike other tools, ClaraAI processes data locally on your Mac and gives you complete control over what gets shared."
        }
      ]
    },
    {
      category: "Features",
      questions: [
        {
          question: "What is the Vision Frame?",
          answer: "The Vision Frame is ClaraAI's signature feature - a transparent, draggable window that hovers over your screen and provides AI-powered insights about whatever you're looking at. It can analyze documents, code, images, and more, offering contextual assistance without interrupting your workflow."
        },
        {
          question: "Which AI providers does ClaraAI support?",
          answer: "ClaraAI integrates with multiple AI providers including OpenAI (GPT-4), Google Gemini, Grok, and local Ollama models. The app intelligently routes requests and provides fallback options to ensure you always get the best possible response."
        },
        {
          question: "How does the audio transcription work?",
          answer: "ClaraAI captures both microphone input and system audio in real-time, transcribing conversations and meetings. This allows you to ask questions about what was discussed, get summaries, and extract key insights from your audio content."
        },
        {
          question: "What automation features are available?",
          answer: "ClaraAI includes AppleScript and JavaScript automation capabilities, allowing you to create custom workflows and automate common tasks. The AI can help you write and execute automation scripts based on your specific needs."
        }
      ]
    },
    {
      category: "Privacy & Security",
      questions: [
        {
          question: "Is my data private?",
          answer: "Yes! ClaraAI is built with privacy as a core principle. Your data stays on your Mac by default, with local processing for most features. When you choose to use cloud AI services, you control exactly what gets shared and can review all data before transmission."
        },
        {
          question: "What data does ClaraAI collect?",
          answer: "ClaraAI collects minimal data and only what you explicitly allow. Screen analysis happens locally, and audio transcription can be processed entirely on your device. Any data sent to cloud AI services is clearly indicated and requires your consent."
        },
        {
          question: "Can I use ClaraAI offline?",
          answer: "Many of ClaraAI's features work offline, including local AI processing with Ollama models, basic screen analysis, and audio transcription. Cloud AI features require an internet connection, but you have full control over when these are used."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          question: "What are the system requirements?",
          answer: "ClaraAI requires macOS 13.0 or later, with recommended specifications including 8GB RAM, 2GB free disk space, and a modern Intel or Apple Silicon processor. The app is optimized for performance and efficiency."
        },
        {
          question: "How do the global shortcuts work?",
          answer: "ClaraAI provides three main global shortcuts: ⌘A for instant screen analysis, ⌘Q for asking questions, and ⌘L for listening to questions. These shortcuts work system-wide and can be customized to your preferences."
        },
        {
          question: "Can I integrate ClaraAI with other apps?",
          answer: "Yes! ClaraAI integrates seamlessly with popular communication platforms like Discord, Slack, Google Meet, Zoom, and FaceTime. The Vision Frame can analyze content from these apps and provide contextual assistance."
        }
      ]
    },
    {
      category: "Support",
      questions: [
        {
          question: "How can I get help with ClaraAI?",
          answer: "You can contact our support team at support@claraai.net for any questions or issues. We're committed to providing excellent customer service and will get back to you as soon as possible."
        },
        {
          question: "Will there be a mobile version?",
          answer: "While ClaraAI is initially focused on desktop productivity, we're exploring mobile options for the future. The Vision Frame concept and AI integration could be adapted for mobile workflows, but our current priority is perfecting the macOS experience."
        },
        {
          question: "Can I suggest new features?",
          answer: "Absolutely! We welcome feature suggestions and feedback from our community. You can reach out to us at support@claraai.net with your ideas. We regularly review user feedback to prioritize new features and improvements."
        }
      ]
    }
  ];

  const filteredFAQ = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(item =>
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>ClaraAI FAQ — Vision Frame, Audio, and Privacy</title>
        <meta name="description" content="Answers about ClaraAI's Vision Frame, integrations, audio processing, and privacy-first design." />
        <link rel="canonical" href="https://claraai.app/faq" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20">
        {/* Dynamic Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900"></div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)`,
            backgroundSize: '100% 100%'
          }}></div>
        </div>
        
        {/* Floating UI Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-24 h-24 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to="/"
                className="inline-flex items-center text-white hover:text-blue-300 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Link>
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6"
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium text-sm">
                <HelpCircle className="w-4 h-4 mr-2 text-blue-300" />
                Frequently Asked Questions
              </div>
            </motion.div>
            
            {/* Main Headline */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            >
              FAQ
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Find answers to common questions about ClaraAI and learn how to make the most of our revolutionary productivity features.
            </motion.p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="max-w-md mx-auto mb-8"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {filteredFAQ.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No questions found</h3>
                <p className="text-gray-500">Try adjusting your search terms or browse all categories below.</p>
              </motion.div>
            ) : (
              filteredFAQ.map((category, categoryIndex) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                  className="mb-12"
                >
                  <h2 className="text-3xl font-bold text-clara-gray-900 mb-8 text-center">
                    {category.category}
                  </h2>
                  
                  <div className="space-y-4">
                    {category.questions.map((item, index) => {
                      const globalIndex = `${categoryIndex}-${index}`;
                      const isOpen = openItems.has(globalIndex);
                      
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: (categoryIndex * 0.1) + (index * 0.05) }}
                          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                        >
                          <button
                            onClick={() => toggleItem(globalIndex)}
                            className="w-full px-6 py-5 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-clara-blue-300"
                          >
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-clara-gray-900 pr-4">
                                {item.question}
                              </h3>
                              <motion.div
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex-shrink-0"
                              >
                                <ChevronDown className="w-5 h-5 text-clara-gray-400" />
                              </motion.div>
                            </div>
                          </button>
                          
                          <motion.div
                            initial={false}
                            animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-5">
                              <p className="text-clara-gray-600 leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-clara-blue-600 to-clara-blue-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Still have questions?
            </h2>
            <p className="text-xl text-clara-blue-100 mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is here to help you get the most out of ClaraAI.
            </p>
            <motion.a
              href="mailto:support@claraai.net?subject=FAQ Support"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center bg-white text-clara-blue-600 font-semibold py-4 px-8 rounded-xl hover:bg-clara-blue-50 transition-all duration-300 shadow-lg"
            >
              Contact Support
            </motion.a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FAQ;
